<script>
/* eslint-disable no-unused-vars */
// import '@sigmacloud/sigma-client'

/* New 1099 Workflow
 * User Step 1:
 *   Select Tax Year and date boundaries (by payment_date)
 *   Get transactions that match above date boundaries
 *   From those transactions, filter out the list of vendors
 * User Step 2:
 *   Allow Users to edit certain vendor info, e.g. tax id, entity type, and possibly status
 * User Step 3:
 *   Next, show transaction details for above vendors and for 1099-able entity types
 *   Allow Users to edit certain transaction info, e.g. taxcode
 * User Step 4:
 *   Print a report/forms
 */

import Vue from 'vue'
import { BootstrapVue } from 'bootstrap-vue'
Vue.use(BootstrapVue)

import VendorList from '@/components/SigmaClientNew/VendorList.vue'
import VendorTransactionsList from '@/components/SigmaClientNew/VendorTransactionsList.vue'
import { SettingsResource, VendorResource, AddressResource, StateResource, CityResource } from '@sigmacloud/sigma-client/dist/resources'

import DateMixins from '@/Mixins/SigmaClientNew/DateMixins'
import { AgGridVue } from 'ag-grid-vue'
import { reportMixins } from '@/Mixins/Reports'
import DateField from '@/components/InputComponents/DateField.vue'
import { sortObjectByProperty, sortResourcesByProperty } from '@/utils/helpers/ObjectSorting'

export default {
  name: 'TaxReport',
  components: { DateField, VendorList, VendorTransactionsList, AgGridVue },
  mixins: [ reportMixins, DateMixins ],
  data () {
    const { getJasperHost } = this.$store.getters
    return {
      tab: 'options', // initial view
      currentStep: 1,
      iteration: 0,
      loading: false,
      isModified: true, // To prevent users from leaving the screen
      rowData: {
        vendors: undefined,
        transactionLines: undefined
      },
      sigmaQueryData: undefined,
      allEntityTypes: [ 'NOT_1099', 'CORPORATION', 'LLC', 'LLC_CORP', 'LEGAL_FIRM', 'PARTNERSHIP', 'SOLE_PROPRIETOR_SSN', 'SOLE_PROPRIETOR_EIN', 'INDIVIDUAL', 'OTHER', 'UNKNOWN' ],

      reportOptions: {
        transactionStatuses: [ 'posted', 'closed' ],
        accountQuickPick: 'all',
        type: 'detail',
        sort: 'account',
        status: 'all',
        format: 'pdf',
        print1099: 'No',
        numCopiesMisc: 2, // only for recipient copies.
        numCopiesNec: 2 // only for recipient copies.
      },

      allCodes: {},    // Show codes, e.g. accounts, series, locations, sets, etc.
      bankRelatedAccounts: [],     // Do not show bank related accounts
      // Needed for Sigma Query
      selectedCodes: {
        taxYear: '2024',
        time_posted_start: undefined,
        time_posted_end: undefined,
        payment_date_start: '2024-01-01',
        payment_date_end: '2024-12-31',
        company_id: undefined,
        production_id: undefined,
        account_id: [],
        source_code_id: [],
        hideNonTaxable: true,
        enableTaxableAmount: true
        // showTaxableCostsOnly: false,
      },
      jasperSettings: {
        reportServer: `${getJasperHost}rest_v2/reports/Revolution/Phoenix/`,
        reportName: '1099_detail'
        // reportName: '1099_misc_2021_recipient'
      },
      transactionTypes: {     // Types of transactions and whether we need them or not
        'aps': { name: 'AP', include: true },
        'jes': { name: 'JE', include: true },
        'pcs': { name: 'PC', include: true },
        'cks': { name: 'CK', include: false },
        'pos': { name: 'PO', include: false }
      },

      originalCacheTimes: {
        // Numbers are here for default, but should be overwritten in beforeMount with actual default resource settings
        vendor: 60,
        address: 60,
        state: 60,
        city: 60
      },

      reportOutputFormats: [
        { value: 'pdf', label: 'PDF' },
        { value: 'csv', label: 'CSV' }
      ],
      disableReportRun: false, // Disable run report button if false
      reportJson: undefined,
      prettyJson: undefined,
      showJson: false,
      reportNotif: undefined,
      taxYearChanges: [
        {
          description: 'FATCA filing requirement',
          code_2021: 'N/A',
          code_2022: '13'
        },
        {
          description: 'Excess golden parachute payments',
          code_2021: '13',
          code_2022: '14'
        },
        {
          description: 'Nonqualified deferred compensation',
          code_2021: '14',
          code_2022: '15'
        },
        {
          description: 'State tax withheld',
          code_2021: '15',
          code_2022: '16a'
        },
        {
          description: 'State/Payer’s state no.',
          code_2021: '16',
          code_2022: '17'
        },
        {
          description: 'State Income',
          code_2021: '17',
          code_2022: '18'
        }
      ]
    }
  },

  computed: {
    resources () {
      return {
        SettingsResource,
        VendorResource,
        AddressResource,
        StateResource,
        CityResource
      }
    },
    height () {
      // Reducing by 3 % to handle sigma-frontend navbar
      return window.innerHeight * 0.73 + 'px'
    },

    username () { return this.$store.getters.getUser.username },

    includedTypes () {
      if (this.selectedCodes.source_code_id.length) this.transactionTypes.jes.include = true
      return Object.values(this.transactionTypes).filter(types => types.include === true).map(type => type.name)
    },

    accounts () {
      let accountsNew = []
      for (let i in this.allCodes.accounts) {
        if (this.allCodes.accounts[i].type.toLowerCase() === 'header') continue
        accountsNew[i] = this.allCodes.accounts[i]
        accountsNew[i]['display_name'] = this.allCodes.accounts[i]['code'] + ' - ' + this.allCodes.accounts[i]['name']
      }
      return accountsNew
    },

    taxableVendors () {
      const entityTypes = [ 'SOLE_PROPRIETOR_SSN', 'SOLE_PROPRIETOR_EIN', 'INDIVIDUAL', 'LLC', 'LEGAL_FIRM', 'PARTNERSHIP', 'OTHER' ]
      if (this.rowData.vendors) {
        const taxableVendorResources = this.rowData.vendors.filter(({ attributes }) => entityTypes.includes(attributes.entity_type))
        return taxableVendorResources && taxableVendorResources.length ? taxableVendorResources.map((vendor) => vendor.id) : false
      } else {
        return false
      }
    },

    unknownVendors () {
      const entityTypes = [ 'UNKNOWN' ]
      if (this.rowData.vendors) {
        const unknownVendorResources = this.rowData.vendors.filter(({ attributes }) => entityTypes.includes(attributes.entity_type))
        return unknownVendorResources && unknownVendorResources.length ? unknownVendorResources.map((vendor) => vendor.id) : false
      } else {
        return false
      }
    },

    missingTin () {
      if (!this.taxableVendors) {
        return false
      }
      const entityTypes = [ 'SOLE_PROPRIETOR_SSN', 'SOLE_PROPRIETOR_EIN', 'INDIVIDUAL', 'LLC', 'LEGAL_FIRM', 'PARTNERSHIP', 'OTHER' ]
      const taxableVendorResources = this.rowData.vendors.filter(({ attributes }) => entityTypes.includes(attributes.entity_type) && !attributes.tax_id)
      return taxableVendorResources && taxableVendorResources.length ? taxableVendorResources.map((vendor) => vendor.id) : false
    }
  },

  watch: {},

  methods: {
    // Step 1: Get all transactions within the payment date range to find matching vendors
    async getData () {
      this.loading = true
      this.disableReportRun = true
      this.rowData.vendors = undefined
      await this.getAllVendors()
      this.sigmaQueryData = await this.getMatchingTransactionData()
      if (this.sigmaQueryData) {
        const matchingVendors = this.sigmaQueryData.map((vendor) => vendor.vendor_id)
        this.iteration++
        this.tab = 'vendors'
        this.currentStep = 2
      }
      this.disableReportRun = false
      this.loading = false
    },

    // Step 3. After users review/correct vendors, show transactions for 1099-able vendors
    async getTaxableLineData () {
      this.saveVendorList()
      if (this.unknownVendors && this.missingTin) this.createNotification('Warning', 'There are still UNKNOWN vendor types and taxable vendors missing Tax IDs.  Please correct before reporting.', 'warning', null, null, null, 5000)
      else if (this.missingTin) this.createNotification('Warning', 'There are still taxable vendors with missing Tax IDs.  Please correct before reporting.', 'warning', null, null, null, 5000)
      else if (this.unknownVendors) this.createNotification('Warning', 'There are still UNKNOWN vendor types.  Please correct before reporting.', 'warning', null, null, null, 5000)

      if (this.rowData.transactionLines) this.saveVendorTransactionsList()

      this.setTransactionLines()
      this.currentStep = 3
      this.iteration++
      this.tab = 'review'
    },

    // Step 4.  Review vendor/transaction summaries
    viewSummary () {
      this.saveVendorList()
      this.saveVendorTransactionsList()
      this.currentStep = 4
      this.iteration++
      this.tab = 'summary'
    },

    // Step 5.  Go to Reports
    viewReports () {
      this.saveVendorList()
      this.saveVendorTransactionsList()
      this.currentStep = 5
      this.iteration++
      this.tab = 'reports'
      if (this.unknownVendors && this.missingTin) this.createNotification('Warning', 'There are still UNKNOWN vendor types and taxable vendors missing Tax IDs.  Please correct before reporting.', 'warning', null, null, null, 5000)
      else if (this.missingTin) this.createNotification('Warning', 'There are still taxable vendors with missing Tax IDs.  Please correct before reporting.', 'warning', null, null, null, 5000)
      else if (this.unknownVendors) this.createNotification('Warning', 'There are still UNKNOWN vendor types.  Please correct before reporting.', 'warning', null, null, null, 5000)
    },

    goBack () {
      switch (this.tab) {
        case 'vendors':
          this.currentStep--
          this.tab = 'options'
          break

        case 'review':
          this.currentStep--
          this.tab = 'vendors'
          break

        case 'summary':
          this.currentStep--
          this.tab = 'review'
          break

        case 'reports':
          this.currentStep--
          this.tab = 'summary'
          break

        default:
          this.currentStep = 1
          this.tab = 'options'
      }
    },

    resetPage () {
      this.currentStep = 1
      this.tab = 'options'
      this.sigmaQueryData = undefined
    },

    saveVendorList () {
      this.loading = true
      this.$refs.vendorList.save()
      this.loading = false
    },

    saveVendorTransactionsList () {
      this.loading = true
      this.$refs.vendorTransactionList.save()
      this.loading = false
    },

    // Get all vendor resources to compare against data
    async getAllVendors () {
      let queryFilters = { page_size: 100 }
      // It's much more efficient to get all addresses now instead of resolving them individually
      await this.resources.StateResource.filterAll(queryFilters)
      await this.resources.CityResource.filterAll(queryFilters)
      await this.resources.AddressResource.filterAll(queryFilters)
      await this.resources.VendorResource.filterAll(queryFilters)
    },

    // Use sigmaQuery to get the data by payment date
    async getMatchingTransactionData () {
      let reportResult = await this.getReportData('taxreport')
      this.prettyJson = JSON.stringify(reportResult, null, 2)
      if (!reportResult || !Array.isArray((reportResult.data.report || {}).data)) {
        this.createNotification('Info', 'No Data to Report. ', 'info')
        return undefined
      }

      // Replace encrypted data with actual TIN and figure out the default address
      let matchingVendors = []
      for (const i in reportResult.data.report.data) {
        let vendorData = reportResult.data.report.data[i]
        const vendorResource = await this.resources.VendorResource.detail(vendorData.vendor_id) || null
        if (!vendorResource) continue
        const defaultAddressField = 'address_' + (vendorResource.attributes.default_address || 'CONTACT').toLowerCase()
        const defaultAddressAttr = vendorResource.attributes[defaultAddressField]
        const defaultAddressId = defaultAddressAttr ? defaultAddressAttr.id : undefined
        if (defaultAddressId) {
          let defaultAddressResource = await this.resources.AddressResource.detail(defaultAddressId)
          if (defaultAddressResource.attributes.state) await this.resources.StateResource.detail(defaultAddressResource.attributes.state)
          if (defaultAddressResource.attributes.city) await this.resources.CityResource.detail(defaultAddressResource.attributes.city)
        }
        vendorData.vendor_tax_id = vendorResource ? vendorResource.get('tax_id') : ''
        vendorData.vendor_tax_id_last4 = vendorData.vendor_tax_id ? 'x-' + String(vendorData.vendor_tax_id).slice(-4) : ''
        matchingVendors.push(vendorResource)
      }
      this.rowData.vendors = sortResourcesByProperty(matchingVendors, 'name', 1)
      return reportResult.data.report.data
    },

    async setTransactionLines () {
      // Show only taxable vendors or all?
      const vendorDetails = this.selectedCodes.hideNonTaxable
        ? this.sigmaQueryData.filter((data) => (this.taxableVendors || []).includes(data.vendor_id) && data.details.length)
        : this.sigmaQueryData.filter((data) => data.details.length)

      this.rowData.transactionLines = undefined
      this.rowData.transactionLines = vendorDetails.map(({ details }) => details).flat()
    },

    // Build output for Jasper Reporting
    buildJson (reportName, copies = 1) {
      if (!reportName) reportName = this.jasperSettings.reportName
      let data = []
      let currentTypeLevel = 0
      let currentNodeLevel = 0
      const gridApi = this.$refs.vendorSummary.gridApi
      let firstRecord
      // const totalRow = (gridApi.getPinnedBottomRow(2) || {}).data

      // Figure out date ranges
      const dateStart = this.selectedCodes.payment_date_start
      const dateEnd = this.selectedCodes.payment_date_end

      // Main report object
      let json = {
        report: {
          report_title: '1099 Detailed Audit Report',
          report_subtitle: 'Listed by Vendor',
          report_name: reportName,
          // company: 'All',
          // project: joinResourceLabels(this.reportOptions.productions),
          date_run: this.dateObjectToString(new Date()),
          period: this.selectedCodes.taxYear,
          printed_by: this.username,
          date_start: dateStart,
          date_end: dateEnd,
          currency: 'USD'
        }
      }

      gridApi.forEachNodeAfterFilter((row) => {
        const level = row.level
        const rowData = row.aggData || null
        let rowDataFixed = {}
        if (rowData) {
          const detail = row.childrenAfterGroup && row.childrenAfterGroup.length ? row.childrenAfterGroup : []
          firstRecord = detail[0].data || {}
          const vendor = this.resources.VendorResource.getCached(firstRecord.vendor_id_line).resource || null
          rowDataFixed['NEC-1'] = parseFloat(rowData['NEC-1'] || 0)
          rowDataFixed['NEC-4'] = parseFloat(rowData['NEC-4'] || 0)
          rowDataFixed['NEC-5'] = parseFloat(rowData['NEC-5'] || 0)
          rowDataFixed['NEC-6'] = rowData['states'] || ''
          rowDataFixed['NEC-7'] = parseFloat(rowData['NEC-7'] || 0)
          rowDataFixed['1099-1'] = parseFloat(rowData['1099-1'] || 0)
          rowDataFixed['1099-2'] = parseFloat(rowData['1099-2'] || 0)
          rowDataFixed['1099-3'] = parseFloat(rowData['1099-3'] || 0)
          rowDataFixed['1099-4'] = parseFloat(rowData['1099-4'] || 0)
          rowDataFixed['1099-5'] = parseFloat(rowData['1099-5'] || 0)
          rowDataFixed['1099-6'] = parseFloat(rowData['1099-6'] || 0)
          rowDataFixed['1099-7'] = parseFloat(rowData['1099-7'] || 0)
          rowDataFixed['1099-8'] = parseFloat(rowData['1099-8'] || 0)
          rowDataFixed['1099-9'] = parseFloat(rowData['1099-9'] || 0)
          rowDataFixed['1099-10'] = parseFloat(rowData['1099-10'] || 0)
          rowDataFixed['1099-11'] = parseFloat(rowData['1099-11'] || 0)
          rowDataFixed['1099-12'] = parseFloat(rowData['1099-12'] || 0)
          rowDataFixed['1099-13'] = parseFloat(rowData['1099-13'] || 0)
          rowDataFixed['1099-14'] = parseFloat(rowData['1099-14'] || 0)
          rowDataFixed['1099-15'] = parseFloat(rowData['1099-15'] || 0)
          rowDataFixed['1099-16'] = rowData['states'] || ''
          rowDataFixed['1099-16a'] = parseFloat(rowData['1099-16a'] || 0)
          rowDataFixed['1099-17'] = parseFloat(rowData['1099-17'] || 0)
          rowDataFixed['1099-18'] = parseFloat(rowData['1099-18'] || 0)
          rowDataFixed.taxable_total = parseFloat(rowData.taxable || 0)
          rowDataFixed.nontax_total = parseFloat(rowData.nonTaxable || 0)
          rowDataFixed.non_nectax_total = rowDataFixed.taxable_total - rowDataFixed['NEC-1']
          rowDataFixed.company_id = firstRecord.company_id
          rowDataFixed.company = firstRecord.company
          rowDataFixed.company_name = firstRecord.company_name
          rowDataFixed.company_tax_id = firstRecord.company_tax_id
          rowDataFixed.company_address1 = firstRecord.company_address1
          rowDataFixed.company_address2 = firstRecord.company_address2
          rowDataFixed.company_city = firstRecord.company_city
          rowDataFixed.company_state = firstRecord.company_state
          rowDataFixed.company_postal = firstRecord.company_postal
          rowDataFixed.company_phone = firstRecord.company_phone
          rowDataFixed.vendor_name = vendor.attributes.payee_name || firstRecord.vendor_name
          rowDataFixed.vendor_number = firstRecord.vendor_number_line
          rowDataFixed.vendor_alias = firstRecord.vendor_alias
          rowDataFixed.entity_type = firstRecord.entity_type
          rowDataFixed.vendor_tax_id = vendor.attributes.tax_id || firstRecord.vendor_tax_id
          rowDataFixed.vendor_address1 = firstRecord.ven_address1
          rowDataFixed.vendor_address2 = firstRecord.ven_address2
          rowDataFixed.vendor_address_full = firstRecord.ven_address_full
          rowDataFixed.vendor_city = firstRecord.ven_city
          rowDataFixed.vendor_state = firstRecord.ven_state
          rowDataFixed.vendor_postal = firstRecord.ven_postal
          rowDataFixed.fatca_filing_requirement = vendor.attributes.fatca_filing_requirement ? '1' : ''
          if (reportName.includes('1099_misc_2024') || reportName.includes('1099_nec_2024')) {
            rowDataFixed.period = this.selectedCodes.taxYear
          } else {
            rowDataFixed.period = this.selectedCodes.taxYear.slice(-2)
          }

          rowDataFixed.details = detail.map((line) => {
            line.data.vendor_tax_id = rowDataFixed.vendor_tax_id
            return line.data
          })

          if (['1099_detail', '1099_summary', '1099_detail'].includes(reportName)) {
            rowDataFixed.details = rowDataFixed.details.filter((d) => d.tax_code !== '1099-15a')
          }
          // For Track1099.com
          rowDataFixed.vendor_email = vendor.attributes.email ? vendor.attributes.email : vendor.attributes.address_contact ? vendor.attributes.address_contact.contact_email : ''
          rowDataFixed.tin_type = firstRecord.entity_type
          switch (firstRecord.entity_type) {
            case 'SOLE_PROPRIETOR_SSN':
            case 'INDIVIDUAL':
              rowDataFixed.tin_type = '2'
              break

            case 'CORPORATION':
            case 'LLC':
            case 'LLC_CORP':
            case 'LEGAL_FIRM':
            case 'SOLE_PROPRIETOR_EIN':
              rowDataFixed.tin_type = '1'
              break

            default:
              rowDataFixed.tin_type = ''
          }

          let newRow
          if (reportName.match(/^1099_nec/) && ((this.selectedCodes.enableTaxableAmount && rowDataFixed['NEC-1'] >= 600) || !this.selectedCodes.enableTaxableAmount)) {
            newRow = rowDataFixed
          } else if (reportName.match(/^1099_misc/) && ((this.selectedCodes.enableTaxableAmount && rowDataFixed.non_nectax_total >= 600) || !this.selectedCodes.enableTaxableAmount)) {
            newRow = rowDataFixed
          } else if (reportName.match(/^1099_misc/) || reportName.match(/^1099_nec/)) {
            // do nothing
            // console.log('Not including ' + rowDataFixed.vendor_name)
          } else {
            newRow = rowDataFixed
          }

          // Add copies
          if (newRow) {
            for (let i = 0; i < copies; i++) {
              data.push(newRow)
            }
          }
        }
      })

      // Add Grand Totals
      const fieldsToTotal = [ 'NEC-1', 'NEC-4', 'NEC-5', 'NEC-7', '1099-1', '1099-2', '1099-3', '1099-4', '1099-5', '1099-6', '1099-7', '1099-8', '1099-9', '1099-10', '1099-11', '1099-12', '1099-13', '1099-14', '1099-15', '1099-17' ]
      data.forEach((record) => {
        for (const field in record) {
          if (fieldsToTotal.includes(field)) {
            json.report[field + '_grand_total'] = this.sumRecursive(data, field) / copies
          }
          json.report.taxable_grand_total = this.sumRecursive(data, 'taxable_total') / copies
          json.report.nontax_grand_total = this.sumRecursive(data, 'nontax_total') / copies
          json.report.non_nectax_grand_total = this.sumRecursive(data, 'non_nectax_total') / copies
          json.report.grand_total = parseFloat(json.report.taxable_grand_total) + parseFloat(json.report.nontax_grand_total)
          json.report.num_items = data.length || ''
        }
      })

      json.report.company = firstRecord.company_name
      json.report.project = firstRecord.production_name
      json.report.data = data
      return json
    },

    async createReport (reportName, copies = 1) {
      if (!reportName) reportName = this.jasperSettings.reportName
      this.loading = true
      const json = this.buildJson(reportName, copies)
      this.reportJson = json
      this.prettyJson = JSON.stringify(json, null, 2)
      const resJsonUrl = await this.getJsonUrl(json)
      // PDF version of the report
      let report = this.jasperSettings.reportServer + reportName + '.pdf?net.sf.jasperreports.json.source=' + resJsonUrl
      // HTML version of the report
      let preview = this.jasperSettings.reportServer + reportName + '.html?net.sf.jasperreports.json.source=' + resJsonUrl
      const reportUrl = await this.getReportUrl(report)
      this.createNotification('Success', 'Report is Ready. ', 'success', reportUrl)
      this.loading = false
    },

    exportToExcel () {
      if (this.tab === 'review') {
        this.$refs.vendorTransactionList.exportToExcel()
      } else {
        this.$refs.vendorSummary.exportToExcel()
      }
    },

    buildCsvData (reportName) {
      if (!reportName) reportName = this.jasperSettings.reportName
      const reportJson = this.buildJson(reportName)
      // Different formats
      const detailedCsvMap = { 'Company': 'company_name', 'Company Tax Id': 'company_tax_id', 'Company Address 1': 'company_address1', 'Company Address 2': 'company_address2', 'Company City': 'company_city', 'Company State': 'company_state', 'Company Postal': 'company_postal', 'Company Telephone': 'company_phone', 'Production': 'production_name', 'Vendor Number': 'vendor_number_line', 'Vendor Name': 'vendor_name', 'Vendor Tax Type': 'entity_type', 'Vendor Tax ID': 'vendor_tax_id', 'Vendor Address 1': 'ven_address1', 'Vendor Address 2': 'ven_address2', 'Vendor City': 'ven_city', 'Vendor State': 'ven_state', 'Vendor Postal': 'ven_postal', 'Trans Type': 'trans_type', 'Trans #': 'trans_number', 'Document Number': 'doc_number', 'Document Date': 'doc_date', 'Trans Description': 'transaction_description', 'Line Description': 'line_desc', 'Posting Date': 'transaction_time_posted', 'Effective Date': 'transaction_effective_date', 'Period': 'transaction_period_id', 'Account': 'account', 'Account Name': 'account_name', 'Series': 'series', 'Series Name': 'series_name', 'Location': 'location', 'Location Name': 'location_name', 'Set': 'set', 'Set Name': 'set_name', 'FF1': 'free_field_1', 'FF2': 'free_field_2', 'FF3': 'free_field_3', 'FF4': 'free_field_4', 'Tax Code': 'tax_code', 'Amount': 'amount', 'Payment Number': 'payment_number', 'Payment Amount': 'transaction_payment_amount' }
      const normalCsvMap = { 'Company': 'company_name', 'Company Tax Id': 'company_tax_id', 'Production': 'production_name', 'Vendor Number': 'vendor_number_line', 'Vendor Name': 'vendor_name', 'Vendor Tax Type': 'entity_type', 'Vendor Tax ID': 'vendor_tax_id', 'Vendor Address': 'ven_address_full', 'Trans Type': 'trans_type', 'Trans #': 'trans_number', 'Document Number': 'doc_number', 'Document Date': 'doc_date', 'Trans Description': 'transaction_description', 'Line Description': 'line_desc', 'Effective Date': 'transaction_effective_date', 'Period': 'transaction_period_id', 'Account': 'account', 'Account Name': 'account_name', 'Series': 'series', 'Location': 'location', 'Set': 'set', 'FF1': 'free_field_1', 'FF2': 'free_field_2', 'Tax Code': 'tax_code', 'Amount': 'amount', 'Payment Number': 'payment_number', 'Payment Amount': 'transaction_payment_amount' }
      const track1099CsvMap = { 'Reference ID (Optional)': '', "Recipient's Name": 'vendor_name', 'Federal ID type (1=EIN, 2=SSN, 3=ITIN, 4=ATIN)': 'tin_type', "Recipient's Federal ID No.": 'vendor_tax_id', "Recipient's Second Name (optional)": 'na', 'Street Address': 'vendor_address1', 'Street Address Line 2': 'vendor_address2', 'City': 'vendor_city', 'State (2 letters)': 'vendor_state', 'Zip': 'vendor_postal', 'Foreign City, Province/State, Postal Code, Country (leave blank if US)': 'na', "Recipient's Email": 'vendor_email', "Acc't No. (optional)": 'na', 'Office Code (optional)': 'na', 'Box 1 Rents': '1099-1', 'Box 2 Royalties': '1099-2', 'Box 3 Other Income': '1099-3', 'Box 4 Fed Income Tax withheld': '1099-4', 'Box 5 Fishing Boat Proceeds': '1099-5', 'Box 6 Medical & Health Care': '1099-6', 'Box 7 (blank = false, 1=true) Payer made direct sales of $5,000 or more of consumer products to a buyer (recipient) for resale': '1099-7', 'Box 8 Substitute Payments': '1099-8', 'Box 9 Crop Insurance Proceeds': '1099-9', 'Box 10 Gross Proceeds Paid to an Attorney': '1099-10', 'Box 11 Fish purchased for resale': '1099-11', 'Box 12 Section 409A deferrals': '1099-12', "Box 13 FATCA filing req't (1=yes, blank=no)": 'fatca_filing_requirement', 'Box 14 Excess golden parachute payments': '1099-14', 'Box 15 Nonqualified deferred compensation': '1099-15', 'Box 16a State Tax withheld': '1099-16a', 'Box 16b Local Tax Withheld': 'na', 'Box 17a State (2-letters)': '1099-16', 'Box 17 Payer State ID No.': 'na', 'Box 17b Locality': 'na', 'Box 18a State Income': '1099-18', 'Box 18b Local Income': 'na', 'Second TIN Notice (1=true, else blank)': 'na' }
      const track1099NecCsvMap = { 'Reference ID (Optional)': '', "Recipient's Name": 'vendor_name', 'Federal ID type (1=EIN, 2=SSN, 3=ITIN, 4=ATIN)': 'tin_type', "Recipient's Federal ID No.": 'vendor_tax_id', "Recipient's Second Name (optional)": 'na', 'Street Address': 'vendor_address1', 'Street Address Line 2': 'vendor_address2', 'City': 'vendor_city', 'State (2 letters)': 'vendor_state', 'Zip': 'vendor_postal', 'Foreign City, Province/State, Postal Code, Country (leave blank if US)': 'na', "Recipient's Email": 'vendor_email', "Acc't No. (optional)": 'na', 'Office Code (optional)': 'na', 'Box 1 Nonemployee compensation': 'NEC-1', 'Box 2 (blank = false, 1=true) Payer made direct sales of $5,000 or more of consumer products to a buyer (recipient) for resale': 'NEC-2', 'Box 4 Federal income tax withheld': 'NEC-4', 'Box 5 State tax withheld': 'NEC-5', 'Box 6 State': 'NEC-6', "Box 6 Payer's State no.": 'na', 'Box 7 State Income': 'NEC-7', 'Second TIN Notice (1=true, else blank)': 'na', 'Box 5b Local Tax Withheld': 'na', 'Box 6b Locality': 'na', 'Box 7b Local Income': 'na' }

      let json, format
      if (reportName.match(/^1099_nec/)) {
        json = reportJson.report.data
        format = track1099NecCsvMap
      } else if (reportName.match(/^1099_misc/)) {
        json = reportJson.report.data
        format = track1099CsvMap
      } else if (reportName.match(/detail/)) {
        json = reportJson.report.data.map(({details}) => details).flat()
        format = detailedCsvMap
      } else {
        json = reportJson.report.data.map(({details}) => details).flat()
        format = normalCsvMap
      }

      const finalCsv = this.createCsv(json, format)
      this.prettyJson = finalCsv
      this.createNotification('Success', 'CSV is Ready. ', 'success', false, true, '1099_Report')
      return finalCsv || false
    },

    onFailed: event => {
      alert('there was an error, please refresh')
    },

    vendorUpdated (data) {
      if (data.vendor) {
        this.sigmaQueryData = this.sigmaQueryData.map(v => { if (v.vendor_id === data.vendor.id) { return { ...v, [data.key]: data.vendor.attributes[data.field], details: v.details.map((d) => { return { ...d, [data.field]: data.vendor.attributes[data.field] } }) } } else { return v } })
      }
    }
  },

  created () {
    // this.buildDataset()
    // Load data to be used for validation
    this.disableReportRun = true
    this.getCompanyData()
      .then(response => {
        this.allCodes = Object.assign({}, response.data)
        return this.allCodes.banks
      })
      .then(banks => {
        banks.map(bank => {
          this.bankRelatedAccounts.push(bank.default_account_cash_id)
          this.bankRelatedAccounts.push(bank.default_account_clearing_id)
          this.bankRelatedAccounts.push(bank.default_account_deposits_id)
          this.bankRelatedAccounts.push(bank.default_account_discount_id)
        })
        const accountsNoBanks = this.allCodes.accounts.filter(acct => (acct.type.toLowerCase() !== 'header') && (!this.bankRelatedAccounts.includes(acct.id)))
        this.selectedCodes.account_id = accountsNoBanks.map(account => account.id)
      })
      .catch(error => {
        console.log(error)
      })
      .finally(() => {
        this.disableReportRun = false
      })

    window.addEventListener('beforeunload', (e) => {
      if (this.isModified) {
        e.preventDefault()
        e.returnValue = true
      }
    })
  },

  beforeMount () {
    this.originalCacheTimes.vendor = VendorResource.cacheMaxAge
    this.originalCacheTimes.address = AddressResource.cacheMaxAge
    this.originalCacheTimes.state = StateResource.cacheMaxAge
    this.originalCacheTimes.city = CityResource.cacheMaxAge
    VendorResource.cacheMaxAge = 7200000
    AddressResource.cacheMaxAge = 7200000
    StateResource.cacheMaxAge = 7200000
    CityResource.cacheMaxAge = 7200000
  },

  mounted () {},

  beforeDestroy () {
    VendorResource.cacheMaxAge = this.originalCacheTimes.vendor
    AddressResource.cacheMaxAge = this.originalCacheTimes.address
    StateResource.cacheMaxAge = this.originalCacheTimes.state
    CityResource.cacheMaxAge = this.originalCacheTimes.city
    window.addEventListener('beforeunload', (e) => {
      delete e['returnValue']
    })
  }
}
</script>
<template>
  <div style="width: 100%;" v-loading.body="loading" class="sigma-client-new">
    <el-row type="flex" align="middle" justify="end" class="headerRow">
      <el-col>
        <el-row type="flex" align="middle" justify="start" style="font-size: 1.5em;">
          1099 WIZARD
        </el-row>
      </el-col>
      <el-col class="small">
          <a @click="showJson = ! showJson">.&nbsp;</a>
      </el-col>
      <el-col>
        <el-row type="flex" align="middle" justify="end">
          <!-- <el-button type="success" v-if="tab === 'review' || tab === 'summary'" @click="exportToExcel" :disabled="disableReportRun || loading"><span class="bold">Export to Excel</span></el-button> -->
          <el-button type="danger" v-if="tab !== 'options'" @click="resetPage" :disabled="disableReportRun || loading"><span class="bold">Start Over</span></el-button>
          <el-button type="warning" v-if="tab !== 'options'" @click="goBack" :disabled="disableReportRun || loading"><span class="bold">Back</span></el-button>
          <el-button type="info" v-if="tab === 'options'" @click="getData" :disabled="disableReportRun || loading"><span class="bold">Find Eligible Vendors</span></el-button>
          <!-- <el-button type="success" v-if="tab === 'vendors'" @click="saveVendorList" :disabled="disableReportRun || loading">Save Vendors</el-button> -->
          <el-button type="info" v-if="tab === 'vendors'" @click="getTaxableLineData" :disabled="disableReportRun || loading"><span class="bold">Review Transactions</span></el-button>
          <!-- <el-button type="success" v-if="tab === 'review'" @click="saveVendorTransactionsList" :disabled="disableReportRun || loading">Save Transactions</el-button> -->
          <el-button type="info" v-if="tab === 'review'" @click="viewSummary" :disabled="disableReportRun || loading"><span class="bold">View Summary</span></el-button>
          <el-button type="info" v-if="tab === 'summary'" @click="viewReports" :disabled="disableReportRun || loading"><span class="bold">Run Reports</span></el-button>
        </el-row>
      </el-col>
    </el-row>
    <el-row class="headerRow">
      <b-navbar>
        <b-navbar-nav small>
          <b-nav-item @click="tab = 'options'" :active="tab === 'options'" :disabled="currentStep > 1">Step 1: 1099 Options </b-nav-item>
          <b-nav-item @click="tab = 'vendors'" :active="tab === 'vendors'" :disabled="currentStep < 2 || currentStep >= 5">Step 2: Vendor Review </b-nav-item>
          <b-nav-item @click="tab = 'review'" :active="tab === 'review'" :disabled="currentStep < 3 || currentStep >= 5">Step 3: Transaction Review </b-nav-item>
          <b-nav-item @click="tab = 'summary'" :active="tab === 'summary'" :disabled="currentStep < 4">Step 4: Summary </b-nav-item>
          <b-nav-item @click="tab = 'reports'" :active="tab === 'reports'" :disabled="currentStep < 5">Step 5: Reports </b-nav-item>
        </b-navbar-nav>
      </b-navbar>
    </el-row>
    <el-row class="headerRow">
      <b-container v-show="tab === 'options'" fluid class="pt-4" id="options" ref="options">
        <el-row type="flex" align="middle" justify="start" class="headerRow">
          <el-col :span="3" class="right">
            Tax Year
          </el-col>
          <el-col :span="4">
            <el-select v-model="selectedCodes.taxYear" :multiple="false" placeholder="">
              <el-option value="2024">2024</el-option>
              <el-option value="2023">2023</el-option>
              <el-option value="2022">2022</el-option>
              <el-option value="2021">2021</el-option>
              <el-option value="2020">2020</el-option>
            </el-select>
          </el-col>
          <el-col :span="4" class="right">
            Payment Date Start
          </el-col>
          <el-col :span="4">
            <DateField v-model="selectedCodes.payment_date_start" />
          </el-col>
          <el-col :span="4" class="right">
            Payment Date End
          </el-col>
          <el-col :span="4">
            <DateField v-model="selectedCodes.payment_date_end" />
          </el-col>
        </el-row>
        <el-row type="flex" align="middle" justify="start" class="headerRow m-top-20">
        </el-row>
      </b-container>
      <b-container v-show="tab === 'vendors'" fluid class="pt-0" id="vendors" ref="vendors">
        <el-row type="flex" align="middle" justify="start">
          Please review your vendors to make sure the information is correct.  Vendors missing details will be highlighted orange.
          Please remember that corporations and LLC's registered as corporations will not get 1099s.
        </el-row>
        <vendor-list
          class="headerRow"
          name="vendorList"
          ref="vendorList"
          classes="ag-theme-balham"
          width="100%"
          :height="height"
          :iteration="iteration"
          :row-data="rowData.vendors"
          @vendorUpdated="vendorUpdated"
        />
      </b-container>
      <b-container v-show="tab === 'review'" fluid class="pt-0" id="review" ref="review">
        <el-row>
          Please review the transactions for each vendor and review (and if necessary, correct) their 1099 coding.
        </el-row>
        <el-row>
          <el-col :span="1">
            <el-checkbox @change="setTransactionLines" v-model="selectedCodes.hideNonTaxable"></el-checkbox>
          </el-col>
          <el-col :span="18" class="left">
            Show 1099-able Vendors Only? (Will ignore corporations and vendors marked "NOT 1099")
          </el-col>
        </el-row>
        <vendor-transactions-list
          class="headerRow"
          name="vendorTransactionList"
          ref="vendorTransactionList"
          classes="ag-theme-balham"
          width="100%"
          :height="height"
          :iteration="iteration"
          :row-data="rowData.transactionLines"
        />
      </b-container>
      <b-container v-show="tab === 'summary'" fluid class="pt-1" id="summary" ref="summary">
        <el-row>
          If any items below are in red, please review the transactions by hitting "Back." While not necessarily incorrect, changes to the 2020 tax forms make these boxes less likely to have amounts.<br /><strong>Vendors that are greyed out below will not receive a 1099 because they do not reach the $600 threshold, are a corporation, or marked as a non-1099 entity.</strong>
        </el-row>
        <el-row>
          <el-col :span="1">
            <el-checkbox @change="setTransactionLines" v-model="selectedCodes.hideNonTaxable"></el-checkbox>
          </el-col>
          <el-col :span="18" class="left">
            Show 1099-able Vendors Only? (Will ignore corporations and vendors marked "NOT 1099")
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="1">
            <el-checkbox v-model="selectedCodes.enableTaxableAmount"></el-checkbox>
          </el-col>
          <el-col :span="18" class="left">
            Allow only total taxable amounts > $600
          </el-col>
        </el-row>
        <vendor-transactions-list
          name="vendorSummary"
          ref="vendorSummary"
          classes="ag-theme-balham"
          width="100%"
          :height="height"
          :summary="true"
          :iteration="iteration"
          :enableTaxableAmount="selectedCodes.enableTaxableAmount"
          :row-data="rowData.transactionLines"
        />
      </b-container>
      <b-container v-show="tab === 'reports'" fluid class="pt-4" id="reports" ref="reports">
        <el-row type="flex" align="middle" justify="start">
          Please run each report.
        </el-row>
        <el-row class="headerRow" type="flex" align="middle" justify="start">
          <h5>Taxable/Non-Taxable Vendor Report</h5>
        </el-row>
        <el-row class="headerRow" type="flex" align="middle" justify="start">
          <el-button type="info" @click="createReport('1099_detail')" :disabled="disableReportRun || loading"><span class="bold">Detail Report</span></el-button>
          <el-button type="success" plain @click="buildCsvData('1099_summary')" :disabled="disableReportRun || loading">CSV Export</el-button>
          <el-button type="success" plain @click="buildCsvData('1099_detail')" :disabled="disableReportRun || loading">CSV Export (Detailed)</el-button>
        </el-row>
        <hr/>
        <el-row class="headerRow m-top-20" type="flex" align="middle" justify="start">
          <h5>Form IRS 1099-MISC</h5>
        </el-row>
        <el-row class="headerRow" type="flex" align="middle" justify="start">
          1099-MISC Forms (not for use with pre-printed forms)
        </el-row>
        <el-row class="headerRow" type="flex" align="middle" justify="start">
          <el-button type="info" @click="createReport('1099_misc_2024_recipient')" :disabled="disableReportRun || loading"><span class="bold">1099-MISC Recipients</span></el-button>
          <el-button plain type="info" @click="createReport('1099_misc_2024_payer')" :disabled="disableReportRun || loading">1099-MISC Payer (Your) Copies</el-button>
          <el-button plain type="success" @click="createReport('1099_misc_2024_address')" :disabled="disableReportRun || loading"><span class="bold">Optional Recipient Cover Sheet</span></el-button>
        </el-row>
        <el-row class="headerRow" type="flex" align="middle" justify="start">
          Pre-Printed 1099-MISC Forms
        </el-row>
        <el-row class="headerRow" type="flex" align="middle" justify="start">
          <el-button type="danger" @click="createReport('1099_misc_2024_irs')" :disabled="disableReportRun || loading"><span class="bold">1099-MISC</span></el-button>
        </el-row>
        <hr/>
        <el-row class="headerRow m-top-20" type="flex" align="middle" justify="start">
          <h5>Form IRS 1099-NEC</h5>
        </el-row>
        <el-row class="headerRow" type="flex" align="middle" justify="start">
          1099-NEC Forms (not for use with pre-printed forms)
        </el-row>
        <el-row class="headerRow" type="flex" align="middle" justify="start">
          <el-button type="info" @click="createReport('1099_nec_2024_recipient')" :disabled="disableReportRun || loading"><span class="bold">1099-NEC Recipients</span></el-button>
          <el-button plain type="info" @click="createReport('1099_nec_2024_payer')" :disabled="disableReportRun || loading">1099-NEC Payer (Your) Copies</el-button>
          <el-button plain type="success" @click="createReport('1099_nec_2024_address')" :disabled="disableReportRun || loading"><span class="bold">Optional Recipient Cover Sheet</span></el-button>
        </el-row>
        <el-row class="headerRow" type="flex" align="middle" justify="start">
          Pre-Printed 1099-NEC Forms
        </el-row>
        <el-row class="headerRow" type="flex" align="middle" justify="start">
          <el-button type="danger" @click="createReport('1099_nec_2024_irs')" :disabled="disableReportRun || loading"><span class="bold">1099-NEC</span></el-button>
        </el-row>
        <hr/>
        <el-row class="headerRow" type="flex" align="middle" justify="start">
          <h5>Export for Track1099.com</h5>
        </el-row>
        <el-row class="headerRow" type="flex" align="middle" justify="start">
          <el-button type="success" plain @click="buildCsvData('1099_misc')" :disabled="disableReportRun || loading"><span class="bold">Export 1099-MISC</span></el-button>
          <el-button type="success" plain @click="buildCsvData('1099_nec')" :disabled="disableReportRun || loading"><span class="bold">Export 1099-NEC</span></el-button>
        </el-row>
      </b-container>
    </el-row>
    <el-row v-if="showJson">
      <el-col>
        <textarea id="output" style="width: 100%; height: 500px;" v-model="prettyJson"></textarea>
      </el-col>
    </el-row>
    <el-row v-if="showJson">
      <el-col :span="1">
        <el-button plain @click="generateCustomReport" :disabled="disableReportRun">
          Run Customized Report
        </el-button>
      </el-col>
    </el-row>
  </div>
</template>

<style scoped>
  .small { font-size: 8px; }
  .right { float: right; text-align: right; padding: 0px 5px; }
  .left { float: left; text-align: left; padding: 0px 5px; }
  .selectRow { padding: 0px 20px; }
  .section { padding: 40px 20px 0px;  }
  .headerRow { padding: 15px 20px 0px; }
  .m-top-20 { margin-top: 20px; }
  .m-right-20 { margin-right: 20px; }
  .m-left-5 { margin-left: 5px; }
  .m-left-20 { margin-left: 20px; }
  .clear { clear: both; }
</style>
<style>
  @font-face {
    font-family: 'Icons';
    src: url('../../../node_modules/@sigmacloud/sigma-vue-costreporting/dist/fonts/open-iconic.eot');
    src: url('../../../node_modules/@sigmacloud/sigma-vue-costreporting/dist/fonts/open-iconic.eot?#iconic-sm') format('embedded-opentype'), url('../../../node_modules/@sigmacloud/sigma-vue-costreporting/dist/fonts/open-iconic.woff') format('woff'), url('../../../node_modules/@sigmacloud/sigma-vue-costreporting/dist/fonts/open-iconic.ttf') format('truetype'), url('../../../node_modules/@sigmacloud/sigma-vue-costreporting/dist/img/open-iconic.svg#iconic-sm') format('svg');
    font-weight: normal;
    font-style: normal;
  }
  sigma-client-new {
    width: 100%;
    background: #ffffff;
    font-family: "Helvetica", Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: left;
  }

  .el-select { display: block; }
  .el-transfer-panel { width:36%; max-width: 350px }
  .el-transfer-panel__body { min-height: 400px }
  .el-transfer-panel__list { min-height: 360px }
  /* border: 1px solid #d1dbe5; box-shadow: 0 2px 4px rgba(0,0,0,.12),0 0 6px rgba(0,0,0,.04); display: inline-block; width: 30%; position: relative; height: 500px; */
</style>



// WEBPACK FOOTER //
// src/components/Reports/TaxReportCustom2024.vue